import * as XLSX from "xlsx";

const worksheetToBlob = (ws: XLSX.WorkSheet): Blob => {
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Sheet1");
  const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
  return new Blob([wbout], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
};

export const buildExcelBlob = (
  content: (string | number | undefined)[][]
): Blob => {
  return worksheetToBlob(XLSX.utils.aoa_to_sheet(content));
};

export const downloadExcelBlob = (blob: Blob, fileName: string): void => {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName.endsWith(".xlsx") ? fileName : `${fileName}.xlsx`;
  a.click();
  window.URL.revokeObjectURL(url);
};

export type ReportColumn<T> = {
  header: string;
  value: (row: T) => string | number | undefined;
  /** Column width in characters (defaults to 20). */
  width?: number;
  /** Marks the column as numeric so its cells get a thousands number format. */
  numeric?: boolean;
};

export type ReportTotalRow = {
  label: string;
  /** 0-based column index where the total value is placed. */
  valueColumnIndex: number;
  value: number;
};

const NUMBER_FORMAT = "#,##0";
const TITLE_ROW = 0;
const HEADER_ROW = 2;

/**
 * Builds a presentable single-sheet report: a merged title row, a spacer, the
 * column headers, the data rows and an optional total row. Column widths and a
 * thousands number format on numeric columns are applied. Note: the community
 * `xlsx` build does not support bold/colors/borders, so styling stops there.
 */
export const buildReportSheet = <T>({
  title,
  columns,
  rows,
  totalRow,
}: {
  title: string;
  columns: ReportColumn<T>[];
  rows: T[];
  totalRow?: ReportTotalRow;
}): Blob => {
  const aoa: (string | number | undefined)[][] = [
    [title],
    [],
    columns.map((column) => column.header),
    ...rows.map((row) => columns.map((column) => column.value(row))),
  ];

  if (totalRow) {
    const line: (string | number | undefined)[] = columns.map(() => undefined);
    line[0] = totalRow.label;
    line[totalRow.valueColumnIndex] = totalRow.value;
    aoa.push(line);
  }

  const ws = XLSX.utils.aoa_to_sheet(aoa);

  const lastColumn = Math.max(columns.length - 1, 0);
  ws["!merges"] = [
    { s: { r: TITLE_ROW, c: 0 }, e: { r: TITLE_ROW, c: lastColumn } },
  ];
  ws["!cols"] = columns.map((column) => ({ wch: column.width ?? 20 }));

  const firstDataRow = HEADER_ROW + 1;
  const lastDataRow = firstDataRow + rows.length - 1 + (totalRow ? 1 : 0);
  columns.forEach((column, colIndex) => {
    if (!column.numeric) return;
    for (let r = firstDataRow; r <= lastDataRow; r++) {
      const cell = ws[XLSX.utils.encode_cell({ r, c: colIndex })];
      if (cell && typeof cell.v === "number") cell.z = NUMBER_FORMAT;
    }
  });

  return worksheetToBlob(ws);
};
